import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NewSurveyButton,
  SearchBoxCont,
  TableCont,
  TableFooterCont,
  TableSearchArea,
} from './Table.styles';
import { RoleNames } from '../../constans/routes';
import { useAuth } from '../../hooks/useAuth';
import { IPagingInfo, ISurveyFilters, ISurveyItem } from '../../services/surveys';
import { BodyBoldS } from '../../styles/globals';
import { SpinnerCont } from '../../views/SurveyContents/SurveyContents.styles';
import GoToPage from '../GoToPage/GoToPage';
import NumberOfSurveys from '../NumberOfSurveys/NumberOfSurveys';
import Pagination from '../Pagination/Pagination';
import RowsPerPage from '../RowsPerPage/RowsPerPage';
import SearchBox from '../SearchBox/SearchBox';
import Spinner from '../Spinner/Spinner';
import TableContent from './TableContent/TableContent';

interface ITable {
  listArray: ISurveyItem[];
  metadata: IPagingInfo;
  applyFilters: (filters: ISurveyFilters) => void;
  openCreateModal: () => void;
  loading?: boolean;
}

export const Table: FC<ITable> = ({
  listArray,
  metadata,
  applyFilters,
  loading,
  openCreateModal,
}) => {
  const { t } = useTranslation();
  const { totalItemsCount, totalPageCount, pageNo, itemsPerPage } = metadata;
  const surveysCount = totalItemsCount || 0;
  const pagesCount = totalPageCount || 1;
  const itemsCountPerPage = itemsPerPage || 0;
  const itemList = listArray || [];
  const { userRolesFromToken, isEBHRMember } = useAuth();
  const isNewSurveyButtonAvailable =
    userRolesFromToken.roles?.includes(RoleNames.SurveyAdministrator) ||
    userRolesFromToken.roles?.includes(RoleNames.SurveyConsultant) ||
    userRolesFromToken.roles?.includes(RoleNames.EBHRConsultant);
  const isSurveyConsultant = userRolesFromToken.roles?.includes(RoleNames.SurveyConsultant);

  return (
    <TableCont>
      <TableSearchArea>
        <NumberOfSurveys
          surveysCount={surveysCount}
          tableElementsTitle={t('surveys.table.metadata.surveysName')}
          description={
            isEBHRMember
              ? t('surveys.table.metadata.surveysDescription')
              : isSurveyConsultant
                ? t('surveys.table.metadata.surveysOwnDescription')
                : t('surveys.table.metadata.surveysOrgDescription')
          }
        />
        <SearchBoxCont>
          <SearchBox
            searchPhrase={applyFilters}
            placeholder={t('surveys.table.metadata.search')}
            filterName="search"
          />
          {isNewSurveyButtonAvailable && (
            <NewSurveyButton onClick={openCreateModal}>
              <BodyBoldS data-testid="newSurveyButoon">
                {t('surveys.table.metadata.newSurvey')}
              </BodyBoldS>
            </NewSurveyButton>
          )}
        </SearchBoxCont>
      </TableSearchArea>
      {loading ? (
        <SpinnerCont id="SpinnerCont" data-testid="spinnerContentsTab">
          <Spinner size="large" />
        </SpinnerCont>
      ) : (
        <>
          <TableContent data={itemList} loading={loading} applyFilters={applyFilters} />
          <TableFooterCont>
            <RowsPerPage
              itemsPerPage={itemsCountPerPage}
              pagesCount={pagesCount}
              menu={['10', '15', '25', '50', '75', '100']}
            />
            <Pagination
              activePage={Number(pageNo)}
              totalPageCount={Number(totalPageCount)}
              applyFilters={(pageNo: number) => applyFilters({ pageNo: String(pageNo) })}
            />
            <GoToPage applyFilters={applyFilters} pageLimit={totalPageCount} />
          </TableFooterCont>
        </>
      )}
    </TableCont>
  );
};
export default Table;
